import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, 
    HostBinding, inject, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, 
    TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, QueryList } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormBuilder, 
    UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule, 
    MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Params, Router, RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { debounceTime, filter, map, startWith, Observable, Subject, takeUntil } from 'rxjs';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { DateRanges,FileTypes } from '../../../constants';
import { iconTypes } from 'app/modules/admin/apps/file-manager/file-manager.constants';
import { AssetsSearch, ItemPagination } from 'app/modules/admin/apps/file-manager/file-manager.types';
import { Util } from 'app/models/util';
import { DocumentService } from 'app/services/document.service';
import { Asset } from 'app/models/document.js';
import { FileManagerService } from 'app/modules/admin/apps/file-manager/file-manager.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LabelService } from 'app/services/label.service';
import { Label } from 'app/models/label';
import { labelColorDefs } from 'app/shared/global-constants';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { AsyncPipe } from '@angular/common';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FuseAlertType } from '@fuse/components/alert/alert.types.js';
import { appOptionScroll } from 'app/directives/option-scroll.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface SearchConfig {
    emitKeywords?: boolean; 
    placeholder?: string;
    showTrashFilter?: boolean;
    requireFileName?: boolean;
}

type ResolvedSearchConfig = Required<Omit<SearchConfig, 'requireFileName'>> & Pick<SearchConfig, 'requireFileName'>;

const DEFAULT_SEARCH_CONFIG: ResolvedSearchConfig = {
    emitKeywords: false,
    placeholder: 'Search Files/Documents/Drive',
    showTrashFilter: true,
    requireFileName: true,
};

@Component({
    selector     : 'search',
    templateUrl  : './search.component.html',
    styleUrls: ['./search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs     : 'fuseSearch',
    animations   : fuseAnimations,
    standalone   : true,
    imports      : [
        NgIf, 
        MatButtonModule, 
        MatIconModule, 
        MatSelectModule, 
        TextFieldModule, 
        FormsModule, 
        MatAutocompleteModule, 
        ReactiveFormsModule, 
        MatOptionModule, 
        NgFor, 
        RouterLink, 
        NgTemplateOutlet, 
        MatFormFieldModule, 
        MatInputModule, 
        NgClass, 
        MatCheckboxModule, 
        MatRippleModule, 
        MatMenuModule,
        MatTooltipModule,
        MatChipsModule,
        AsyncPipe,
        appOptionScroll,
        MatDatepickerModule,
        MatProgressSpinnerModule
    ],
    providers    : [
        {
            provide   : MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: () =>
            {
                const overlay = inject(Overlay);
                return () => overlay.scrollStrategies.block();
            },
        }, 
    ],
})

export class SearchComponent implements OnChanges, OnInit, OnDestroy {
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 3;
    @Input() rootFolderId: string = null;
    // Missing keys fall back to defaults
    @Input() set config(value: SearchConfig) {
        this._config = { ...DEFAULT_SEARCH_CONFIG, ...value };
    }
    get config(): ResolvedSearchConfig {
        return this._config;
    }
    private _config: ResolvedSearchConfig = DEFAULT_SEARCH_CONFIG;
    // Parent-driven loading spinner
    @Input() loading: boolean = false;
    @Output() keywordsChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() search: EventEmitter<any> = new EventEmitter<any>();
    @Output() serachAssetClick: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('advancedSearchOrigin') private _advancedSearchOrigin: MatButton;
    @ViewChild('advancedSearchPanel') private _advancedSearchPanel: TemplateRef<any>;
    private _overlayRef: OverlayRef;
    
    opened: boolean = false;
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    advancedSearchForm: UntypedFormGroup;
    advancedSearchFormDefaults= {
        keywords  : '',
        contentType  : 'any',
        dateModified: 'any',
        TRASHED : false,
        isArchived: false,
        isFavourite : false,
    }
    queryParams_advancedSearch: Params;
    dateRanges=DateRanges
    fileTypes=FileTypes
    iconTypes: any = iconTypes;
    page:ItemPagination=new ItemPagination()
    assets:Asset[]=[]
    isLoading:boolean=false
    inputValue: string = '';

    /**
     * @title Chips Autocomplete
     */
    separatorKeysCodes: number[] = [ENTER, COMMA];
    tagCtrl = new FormControl('');
    filteredTags: Observable<Label[]>;
    tags: any[] = [ {id:'',name:'Any'}];
    allTags: any[] = []; // Initialize allTags array
    //allTags: string[] = ['Apple', 'Lemon', 'Lime', 'Orange', 'Strawberry'];
    @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
    announcer = inject(LiveAnnouncer);
    labelColors: any;
    labels: Label[] = [];
    assetsSearch:AssetsSearch=new AssetsSearch()
    dataRange:string='any'
    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    showAlert:boolean=false
    totalSearchResults:number=0
    moreLoading:boolean=false
    searchInterval:any=null;
    isWebComponent:boolean = false;

    get isFileNameRequired(): boolean {
        return this.config.requireFileName
            ?? this.utils.getWebComponentInputParams()?.lpmConfig?.isFileNameRequiredInGlobalSearch
            ?? true;
    }

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _overlay: Overlay,
        private _formBuilder: UntypedFormBuilder,
        private _viewContainerRef: ViewContainerRef,
        private router: Router,
        public utils: Util,
        private docService: DocumentService,
        private _fileManagerService: FileManagerService,
        private _labelService :LabelService,
    )
    {   
        this.isWebComponent = this.utils.getIsWebApp();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any {
        return {
            'search-appearance-bar'  : this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened'          : this.opened,
        };
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef) {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if ( value )
        {
            // Give Angular time to complete the change detection cycle
            setTimeout(() =>
            {
                // Focus to the input element
                value.nativeElement.focus();
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete) {
        this._matAutocomplete = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        // Appearance
        if ( 'appearance' in changes ) {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Prepare the search form with defaults
        this.initSearchForm();
        // Subscribe to the search field value changes
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                takeUntil(this._unsubscribeAll),
                map((value) => {
                    // Set the resultSets to null if there is no value or
                    // the length of the value is smaller than the minLength
                    // so the autocomplete panel can be closed
                    if ( !value || value.length < this.minLength )
                    {
                        this.assets = [];
                    }

                    // Continue
                    return value;
                }),
                // Filter out undefined/null/false statements and also
                // filter out the values that are smaller than minLength
                // Let clears through when emitting
                filter(value => this.config.emitKeywords || (value && value.length >= this.minLength)),
            )
            .subscribe((value) => {
                if (this.config.emitKeywords) {
                    this.keywordsChange.emit(value?.length >= this.minLength ? value : null);
                    return;
                }

                this.assetsSearch.sortColumn = 'FOLDER_CREATED_DATE';
                this.assetsSearch.sortDirection = 'DESCENDING';
                this.assetsSearch.fileType = this.assetsSearch.fileType=='any' ? null : this.assetsSearch.fileType;
                this.assetsSearch.keywords = value;
                this.setGlobalSearchScope();
                this.isLoading = true;
                this.page.page = 0;
               this.getAssets();
            });

            this._fileManagerService.registerIcons();
            // // Get the label colors
            this.labelColors = labelColorDefs;
            // Fetch labels from the service and populate labels array
            this._labelService.getLabels()
                .then(res => {
                    const data=this._labelService.getData(res)
                    // Assuming the response is an array of objects with 'name' property
                    this.labels = (data as any[]).map(label => label);
                    // Initialize filteredTags observable
                    this.filteredTags = this.tagCtrl.valueChanges.pipe(
                        startWith(null),
                        map((tag: any | null) =>
                        {
                        return  (tag ? this._filter(tag) : this.labels.slice())
                        }
                       
                        )
                    );
                })
                .catch(error => {
                    console.error('Error fetching labels:', error);
                });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

         // Dispose the overlay
         if ( this._overlayRef ) {
             this._overlayRef.dispose();
         }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void {   
        // Escape
        if ( event.code === 'Escape' ) {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if ( this.appearance === 'bar' && !this._matAutocomplete.isOpen )
            {
                this.close();
            }
        }
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void {
        // Return if it's already opened
        if ( this.opened ) {
            return;
        }

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void {
        // Return if it's already closed
        if ( !this.opened ) {
            return;
        }

        // Clear the search input
        this.searchControl.setValue('');

        // Close the search
        this.opened = false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
     * Open the advanced search
     * Used in 'bar'
     */
    openAdvancedSearchPanel(event): void {
        // avoids focusing the input after clicking on the close button
        event.stopPropagation();
        // Add your code here to open the advanced search
        console.log('Open the advanced search panel');
         // Return if the notifications panel or its origin is not defined
         if ( !this._advancedSearchPanel || !this._advancedSearchOrigin ) {
             return;
         }
 
         // Create the overlay if it doesn't exist
         if ( !this._overlayRef ) {
             this._createOverlay();
         }
 
         // Attach the portal to the overlay
         this._overlayRef.attach(new TemplatePortal(this._advancedSearchPanel, this._viewContainerRef));
    }
    /**
     * Close the notifications panel
     */
    closeAdvancedSearchPanel(): void {   
        this._overlayRef.detach();
    }

      /**
     * Reset the search form using the default
     */
    reset(): void {
        this.assetsSearch = new AssetsSearch()
        this.tags = [{ id: '', name: 'Any' }];
        // this.advancedSearchForm.reset(this.advancedSearchFormDefaults);
        this.initSearchForm()
        // Parent reloads unfiltered list
        if (this.config.emitKeywords) {
            this.search.emit({ ...this.assetsSearch, fileType: null });
        }
    }
    /**
     * Perform the search
     */
    advancedSearch(): void {
        if (this.advancedSearchForm.invalid) return;
        if (this.isFileNameRequired && this.isSearchInValid())
            return
        
        this.configSearchObj();
        // Emit mode never navigates away
        if(this.isWebComponent || this.config.emitKeywords){
            this.closeAdvancedSearchPanel();
            this.search.emit(this.assetsSearch);
            return;
        }
            this.router.navigate(['/apps/file-manager/search'], { queryParams: this.assetsSearch });   
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._advancedSearchOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

     /**
     * Downloads the specified asset.
     * 
     * @param asset - The asset to download.
     */
    download(asset : Asset): void {
        this._fileManagerService.downloadFile(asset);
    }

     /**
     * Adds a tag to the list of tags.
     * 
     * @param event - The MatChipInputEvent object containing the value of the input.
     */
    add(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
    
        // Add our tag
        if (value) {
          this.tags.push(value);
        }
    
        // Clear the input value
        event.chipInput!.clear();
    
        this.tagCtrl.setValue(null);
    }
    
    /**
     * Removes a tag from the list of tags.
     * @param tag - The tag to be removed.
     */
    remove(tag:any): void {
        this.tags=this.tags.filter(tg=>tg.id!==tag.id)
        this.announcer.announce(`Removed ${tag.name}`);
        this.isSearchInValid()
    }

    /**
     * Handles the selection of an option from the autocomplete dropdown.
     * Adds the selected option to the tags array, clears the tag input field, and resets the tag control value.
     * 
     * @param event The MatAutocompleteSelectedEvent object containing information about the selected option.
     */
    selected(event: MatAutocompleteSelectedEvent): void {
        this.tags.push(event.option.value);
        this.tagInput.nativeElement.value = "";
        this.tagCtrl.setValue(null);
        this.isSearchInValid()
    }

    /**
     * Filters an array of tags based on a given value.
     * 
     * @param value - The value to filter the tags with.
     * @returns An array of filtered tags.
     */
    private _filter(value: any): Label[] {
        const filterValue = value?.name?.toLowerCase()??value?.toLowerCase();
        return this.allTags.filter(tag => tag.name?.toLowerCase().includes(filterValue));
    }

    resetFilter() {
        console.log('Reset filter');
    }

    applyFilter() {
        console.log('Apply filter');
    }

    /**
     * Retrieves labels from the label service and assigns them to the component's labels property.
     * @returns {Promise<void>} A promise that resolves when the labels are retrieved and assigned successfully.
     */
    getLabels() {
        this._labelService.getLabels().then((res) => {
            this.labels = this._labelService.getData(res);
            console.log(this.labels);
        }).catch((error) => {
            console.log(error);
        })
    }

    onTrashToggle(event){
        const IsChecked=event.checked
        this.assetsSearch.status=IsChecked?'TRASH':null
    }

    onStarToggle(event){
        const IsChecked=event.checked
        this.assetsSearch.favourite=IsChecked ? true : null
    }

    onArchiveToggle(event){
        const IsChecked=event.checked
        // this.assetsSearch.fileType=IsChecked?' ARCHIVE':null
    }

    configSearchObj(){

        const fileType = this.advancedSearchForm.get('fileType').value
        const keywords = this.assetsSearch.keywords

        this.assetsSearch.keywords = keywords ? keywords : null
        this.assetsSearch.fileType = fileType == 'any' ? null : fileType
        this.setDateRange();

        this.assetsSearch.sortColumn = 'FOLDER_CREATED_DATE';
        this.assetsSearch.sortDirection = 'DESCENDING';
        this.setLabelSearch()
        this.setGlobalSearchScope()
    }

    // Scope global search to host folder
    setGlobalSearchScope(){
        if (this.config.emitKeywords) return;
        this.assetsSearch.parent = this.utils.getWebComponentInputParams()?.folderId ?? null;
        this.assetsSearch.searchDescendants = true;
    }

    setLabelSearch(){
        const selectedLabelIds=[]
        this.tags.forEach(tag=>{
            if(tag.id){
                selectedLabelIds.push(tag.id)
            }
        })

        this.assetsSearch.labels = (selectedLabelIds.length>0) ? selectedLabelIds : null
    }

    isSearchInValid(): boolean {
        // Name/tag optional when not required
        if (!this.isFileNameRequired) return false;
        const isTagExist: boolean = (this.tags.findIndex(tag => tag.id !== '') == -1) ? false : true
        const isNameExist: boolean = this.advancedSearchForm.get('keywords').value ? true : false
        const invalid:boolean = !(isTagExist || isNameExist)
        this.advancedSearchForm.setErrors({
            fileOrTag: invalid
        })
        return invalid
    }

    navigateToFolder(asset: any) {
        const targetId = asset.type === 'FOLDER' ? asset.id : asset.parent.id;
        
        if (this.isWebComponent) {
            this.serachAssetClick.emit(targetId);
        } else {
            this.router.navigate(['/apps/file-manager/folders/', targetId]);
        }
    }

    searchMoreAssets(){
        this.page.page=this.assets.length/this.page.size
        this.moreLoading=true
        this.getAssets(true)
    }

    getAssets(concat:boolean=false){
        this.docService.getAssets(this.page,this.assetsSearch).then(res=>{
            const data=this.docService.getData(res)

            if(concat){
                this.assets=[...this.assets,...data]
            }

            else
            {   
                this.assets=data
            }

            this.totalSearchResults=this.docService.getCount(res)
            this.isLoading=false
            this.moreLoading=false
        }).catch(error=>{
            console.log(error)
            this.isLoading=false
            this.moreLoading=false
        })
    }
    clearSearchPanel(){ 
        this.searchControl.setValue('');
    }

    setDateRange(){
        if(!this.assetsSearch.start)
        this.assetsSearch.start = this.dataRange == 'any' ? null : this.utils.getDateRange(this.dataRange).startDate
        if(!this.assetsSearch.end)
        this.assetsSearch.end = this.dataRange == 'any' ? null : this.utils.getDateRange(this.dataRange).endDate
    }

    onDateRangeChange(event){
        if(event==='customDate'){
            const today = new Date();
            const fromDate = new Date(today.getFullYear(), 0, 1);
            this.assetsSearch.start = fromDate;
            this.assetsSearch.end = today;
            this.setDateValidation()
            return;
        }
        this.clearDateValidation()
        this.assetsSearch.start = null;
        this.assetsSearch.end = null;
    }

       // Update Custom Date Validation
    setDateValidation() {
        const startDate = this.advancedSearchForm.get('startDate');
        const endDate = this.advancedSearchForm.get('endDate');
        startDate?.setValidators([Validators.required]);
        endDate?.setValidators([Validators.required]);
        startDate?.updateValueAndValidity();
        endDate?.updateValueAndValidity();
    }

    clearDateValidation(){
        const startDate = this.advancedSearchForm.get('startDate');
        const endDate = this.advancedSearchForm.get('endDate');
        startDate?.clearValidators();
        endDate?.clearValidators();
        startDate?.updateValueAndValidity();
        endDate?.updateValueAndValidity();
    }

    initSearchForm() {
        this.advancedSearchForm = this._formBuilder.group({
            keywords  : [this.advancedSearchFormDefaults.keywords, this.isFileNameRequired ? [Validators.required] : []],
            fileType : [this.advancedSearchFormDefaults.contentType],
            dataRange: [this.advancedSearchFormDefaults.dateModified],
            TRASHED : [this.advancedSearchFormDefaults.TRASHED],
            isArchived: [this.advancedSearchFormDefaults.isArchived],
            isFavourite : [this.advancedSearchFormDefaults.isFavourite],
            startDate: [null],
            endDate: [null],
        });

        this.filteredTags = this.tagCtrl.valueChanges.pipe(
            startWith(null),
            map((tag: string | null) =>{
                console.log
             return (  tag ? this._filter(tag) : this.allTags.slice())
            }
            ),
          );

          this.assetsSearch.fileType = 'any';

          this.dataRange = 'any';
    }
}